import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { useForm, ValidationError } from '@formspree/react';

const ContactPage: React.FC = () => {
  const [state, handleSubmit] = useForm("xjkwnovr");

  if (state.succeeded) {
    return (
      <div className="w-full p-6 bg-green-50 rounded-lg">
        <p className="text-green-800 text-center text-lg">
          Bedankt voor uw bericht! We nemen zo spoedig mogelijk contact met u op.
        </p>
      </div>
    );
  }

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-12 bg-navy-900 text-white">
        <div className="container mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold mb-4 text-center"
          >
            Contact
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-center max-w-3xl mx-auto"
          >
            Neem contact met ons op voor al uw elektrotechnische vragen
          </motion.p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="w-16 h-16 bg-navy-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone size={24} className="text-navy-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-navy-800">Telefonisch Contact</h3>
              <p className="text-gray-600 mb-4">
                Voor directe hulp of om een afspraak te maken
              </p>
              <a
                href="tel:0383336549"
                className="text-orange-500 font-semibold text-lg hover:text-orange-600 transition-colors"
              >
                038-3336549
              </a>
              <p className="text-gray-500 mt-2 text-sm">24/7 bereikbaar voor noodgevallen</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="w-16 h-16 bg-navy-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail size={24} className="text-navy-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-navy-800">E-mail</h3>
              <p className="text-gray-600 mb-4">
                Stuur ons een bericht met uw vraag of verzoek
              </p>
              <a
                href="mailto:info@htselectrotechniek.nl"
                className="text-orange-500 font-semibold text-lg hover:text-orange-600 transition-colors"
              >
                info@htselectrotechniek.nl
              </a>
              <p className="text-gray-500 mt-2 text-sm">We reageren binnen 24 uur</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="w-16 h-16 bg-navy-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin size={24} className="text-navy-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-navy-800">Bezoekadres</h3>
              <p className="text-gray-600 mb-4">
                Ons kantoor en werkplaats zijn gevestigd in Kampen
              </p>
              <p className="text-navy-800 font-medium">
                Installatieweg 5-1
                <br />
                8263 BS Kampen
              </p>
              <p className="text-gray-500 mt-2 text-sm">Op afspraak geopend</p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white p-8 rounded-lg shadow-lg"
            >
              <h2 className="text-2xl font-bold mb-6 text-navy-800">Neem Contact Op</h2>
              
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Naam *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                      placeholder="Uw naam"
                    />
                    <ValidationError prefix="Name" field="name" errors={state.errors} />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                      placeholder="Uw e-mailadres"
                    />
                    <ValidationError prefix="Email" field="email" errors={state.errors} />
                  </div>
                </div>
                <div className="mb-6">
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Telefoonnummer *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                    placeholder="Uw telefoonnummer"
                  />
                  <ValidationError prefix="Phone" field="phone" errors={state.errors} />
                </div>
                <div className="mb-6">
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Onderwerp
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                    placeholder="Onderwerp van uw bericht"
                  />
                  <ValidationError prefix="Subject" field="subject" errors={state.errors} />
                </div>
                <div className="mb-6">
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Bericht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                    placeholder="Uw bericht"
                  ></textarea>
                  <ValidationError prefix="Message" field="message" errors={state.errors} />
                </div>
                <button
                  type="submit"
                  disabled={state.submitting}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 w-full disabled:opacity-50 shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-0.5 disabled:hover:translate-y-0"
                >
                  {state.submitting ? 'Bezig met verzenden...' : 'Verstuur bericht'}
                </button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-gray-100 rounded-lg overflow-hidden h-80 mb-8">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2413.7505!2d5.8774364!3d52.5682513!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c879d0a802fb2f%3A0x25f2d6fa069ee07!2sHTS%20Electrotechniek!5e0!3m2!1snl!2snl!4v1710427714841!5m2!1snl!2snl"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen={true}
                  loading="lazy"
                  title="HTS Electrotechniek locatie"
                ></iframe>
              </div>

              <div className="bg-navy-800 p-6 rounded-lg shadow-lg text-white">
                <h3 className="text-xl font-bold mb-4">Openingstijden</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Maandag - Vrijdag</span>
                    <span>08:00 - 17:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Zaterdag</span>
                    <span>Op afspraak</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Zondag</span>
                    <span>Gesloten</span>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <div className="flex items-start">
                    <Clock size={20} className="mr-3 mt-1 text-orange-500" />
                    <div>
                      <p className="font-medium">24/7 Bereikbaarheid</p>
                      <p className="text-gray-300">
                        Voor noodgevallen en storingen zijn wij 24 uur per dag, 7 dagen per
                        week telefonisch bereikbaar.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl font-bold mb-4 text-navy-800"
            >
              Veelgestelde Vragen
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-gray-600 max-w-2xl mx-auto"
            >
              Antwoorden op de meest gestelde vragen over onze diensten
            </motion.p>
          </div>

          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-lg shadow-md mb-4"
            >
              <h3 className="text-lg font-semibold mb-2 text-navy-800">
                Wat zijn de kosten voor een offerte?
              </h3>
              <p className="text-gray-600">
                Onze offertes zijn altijd vrijblijvend en kosteloos. We komen graag bij u 
                langs om de situatie te bekijken en een passende offerte op te stellen.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-lg shadow-md mb-4"
            >
              <h3 className="text-lg font-semibold mb-2 text-navy-800">
                Hoe snel kunnen jullie langs komen voor een storing?
              </h3>
              <p className="text-gray-600">
                Bij noodgevallen streven we ernaar om zo snel mogelijk ter plaatse te zijn. 
                Voor reguliere storingen proberen we binnen 24 uur een afspraak in te plannen.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-6 rounded-lg shadow-md mb-4"
            >
              <h3 className="text-lg font-semibold mb-2 text-navy-800">
                Werken jullie ook in het weekend?
              </h3>
              <p className="text-gray-600">
                Voor noodgevallen en storingen zijn we 24/7 bereikbaar, ook in het weekend. 
                Reguliere werkzaamheden worden meestal op werkdagen uitgevoerd, maar in overleg 
                is werken op zaterdag ook mogelijk.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white p-6 rounded-lg shadow-md mb-4"
            >
              <h3 className="text-lg font-semibold mb-2 text-navy-800">
                Wat kost een NEN 3140 keuring?
              </h3>
              <p className="text-gray-600">
                De kosten voor een NEN 3140 keuring zijn afhankelijk van de grootte en 
                complexiteit van de elektrische installatie. Neem contact met ons op voor 
                een vrijblijvende offerte op maat.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-white p-6 rounded-lg shadow-md"
            >
              <h3 className="text-lg font-semibold mb-2 text-navy-800">
                In welke regio zijn jullie actief?
              </h3>
              <p className="text-gray-600">
                Wij zijn voornamelijk actief in Kampen en omgeving, maar werken ook in de 
                bredere regio zoals Zwolle, IJsselmuiden, Dronten en omstreken. Voor grotere
                projecten zijn we bereid verder te reizen.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;